import { useMemo } from 'react'
import {
  ProductFeatureV3,
  RoundRobinExpectedHostV2,
} from '@chilipiper/api-client/src/user-availability'
import {
  ArrayAlwaysInvitedUser,
  PublicRoundRobinSchedulingLinkV2,
  PublicRoundRobinSchedulingLinkV2Host,
} from '@chilipiper/api-client/src/team-scheduling'
import { UserSlugResolution } from '@chilipiper/api-client/src/firerouter'
import {
  BookerInvitationSettings,
  getBookerInvitationSettings,
  getRoundRobinAndOwnershipExpectedHost,
} from '../../load-availability-options'
import { WithDistribution } from '../types'
import {
  getDistributionIdsFromRoundRobinAssignments,
  transformRoundRobinAssignmentsToRoutingResult,
  applySelectedAssigneesToWithDistribution,
  mapDistributionAssignmentsAttendanceById,
} from '../helpers'
import { getLinkProductFeature } from '../helpers/product-feature'
import { useDistributionsUserList } from '../api-queries'
import { useParsedGuest } from './use-parsed-guest'

type RoundRobinMatch = {
  expectedHost: RoundRobinExpectedHostV2
  roundRobinLinkRoutingResult: WithDistribution
  primaryGuestEmail?: string
  bookerInvitationSettings: BookerInvitationSettings
  alwaysInvitedUsers?: ArrayAlwaysInvitedUser
  host: PublicRoundRobinSchedulingLinkV2Host
}

export const useRoundRobinRouting = ({
  tenantId,
  roundRobinSchedulingLink,
  smartParameterId,
  productFeature,
  selectedAssigneeIds,
  meetingAttemptBookerId,
  bookerFromSmartParameter,
}: {
  tenantId: string
  roundRobinSchedulingLink?: Pick<
    PublicRoundRobinSchedulingLinkV2,
    'assignments' | 'host' | 'alwaysInvitedUsers' | 'bookerInvitation'
  >
  smartParameterId?: string | null
  productFeature?: ProductFeatureV3
  selectedAssigneeIds?: string[]
  meetingAttemptBookerId?: string
  bookerFromSmartParameter?: UserSlugResolution
}): RoundRobinMatch | undefined => {
  const linkProductFeature = getLinkProductFeature(productFeature)

  const parsedGuest = useParsedGuest({
    smartParameterId,
    linkProductFeature,
  })

  const primaryGuestEmail = parsedGuest?.guestEmail
  const productFeatureAssignments =
    linkProductFeature?.type === 'RoundRobinSchedulingLink'
      ? linkProductFeature.assignments
      : undefined

  const linkDistributionIds = roundRobinSchedulingLink?.assignments.map(
    assignment => assignment.distributionId
  )

  const distributionIds = useMemo(() => {
    if (productFeatureAssignments) {
      return getDistributionIdsFromRoundRobinAssignments(
        productFeatureAssignments,
        linkDistributionIds
      )
    }

    return linkDistributionIds
  }, [productFeatureAssignments, linkDistributionIds])

  const distributions = useDistributionsUserList({
    tenantId,
    distributionIds,
    productFeature: 'RoundRobinSchedulingLink',
  })

  const roundRobinLinkRoutingResult = productFeatureAssignments
    ? transformRoundRobinAssignmentsToRoutingResult(
        productFeatureAssignments,
        distributions,
        roundRobinSchedulingLink?.assignments
      )
    : {
        distributions,
        attendance: mapDistributionAssignmentsAttendanceById(roundRobinSchedulingLink?.assignments),
      }

  // Apply selectedAssigneeId to the routing result if provided
  const updatedRoutingResult = applySelectedAssigneesToWithDistribution(
    roundRobinLinkRoutingResult,
    selectedAssigneeIds
  )

  if (
    !updatedRoutingResult ||
    updatedRoutingResult.distributions.length === 0 ||
    !roundRobinSchedulingLink
  ) {
    return undefined
  }

  const bookerInvitationSettings = getBookerInvitationSettings({
    bookerFromSmartParameter,
    bookerInvitation: roundRobinSchedulingLink.bookerInvitation,
    meetingAttemptBookerId,
  })

  const expectedHost = getRoundRobinAndOwnershipExpectedHost({
    bookerInvitationSettings,
    host: roundRobinSchedulingLink.host,
    firstAssignment: { distributionId: updatedRoutingResult.distributions[0].id },
  })

  return {
    primaryGuestEmail,
    roundRobinLinkRoutingResult: updatedRoutingResult,
    expectedHost,
    bookerInvitationSettings,
    alwaysInvitedUsers: roundRobinSchedulingLink?.alwaysInvitedUsers,
    host: roundRobinSchedulingLink.host,
  }
}
